import type { FC, ReactNode } from "react";
import type { NavItemType } from "@/components/application/app-navigation/config";
import { HeaderNavigation } from "@/components/Molecules/HeaderNav/HeaderNav";
import { LeftNav } from "@/components/Molecules/LeftNav/LeftNav";
import type { AccountMode } from "@/components/Molecules/NavCard/account-mode";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import type { Organization } from "@/services/organization";

interface ShellProps {
	/** The main content to render (typically an Outlet) */
	children: ReactNode;
	/** List of navigation items to display */
	items: NavItemType[];
	/** User's display name */
	name: string;
	/** User's email */
	email: string;
	/** Handler for sign out action */
	onSignOut: () => void;
	/** Handler for user profile action */
	onUserProfile: () => void;
	/** First user name for LeftNav */
	user1Name?: string;
	/** Tooltip shown on hover for the organization selector */
	orgNavTooltipTitle?: string;
	/** Second user name for LeftNav */
	user2Name?: string;
	/** Home URL for logo links */
	homeUrl: string;
	/** Custom background color class for active items */
	activeBackgroundClass?: string;
	/** Custom class for the main level icons */
	iconClassName?: string;
	/** Whether to show the first nav card */
	showOrgNavCard?: boolean;
	/** Whether to show the second nav card */
	showAppNavCard?: boolean;
	/** Whether to show the first nav card selector dropdown */
	showOrgNavSelector?: boolean;
	/** Whether to show the second nav card selector dropdown */
	showAppNavSelector?: boolean;
	/** First avatar image source URL or icon component */
	avatar1Src?: string | FC<{ className?: string }>;
	/** First avatar alt text */
	avatar1Alt?: string;
	/** First avatar initials fallback */
	avatar1Initials?: string;
	/** Second avatar image source URL or icon component */
	avatar2Src?: string | FC<{ className?: string }>;
	/** Second avatar alt text */
	avatar2Alt?: string;
	/** Second avatar initials fallback */
	avatar2Initials?: string;
	/** First avatar background color class */
	avatar1BackgroundColor?: string;
	/** Second avatar background color class */
	avatar2BackgroundColor?: string;
	/** Header text to display in LeftNav */
	headerText?: string;
	/** Available organizations */
	availableOrganizations: Organization[];
	/** Current organization */
	currentOrganization: Organization;
	/** Whether to disable the organization selector dropdown */
	disabled?: boolean;
	/** First avatar image source URL */
	userAvatarSrc?: string;
	/** Whether the user is a platform admin */
	isPlatformAdmin?: boolean;
	/** Whether the user is an org admin */
	isOrgAdmin?: boolean;
	/** On main app nav card click handler */
	mainAppNavCardOnClickHandler?: () => void;
	/** On admin app nav card click handler */
	adminAppNavCardOnClickHandler?: () => void;
	/**
	 * Visual variant for the LeftNav.
	 * "brand" — dark gradient sidebar (Admin Portal).
	 * "light" — default light sidebar.
	 */
	variant?: "light" | "brand";
	/** Logo image src for the brand variant header. */
	logoSrc?: string;
	/** Icon-only mark for the collapsed brand state. Defaults to LogoMinimal.png. */
	logoIconSrc?: string;
	/**
	 * Footer account mode matching Figma views.
	 * portal | application | standard
	 */
	accountMode?: AccountMode;
}

export const Shell: FC<ShellProps> = ({
	children,
	items,
	name,
	email,
	onSignOut,
	onUserProfile,
	user1Name,
	orgNavTooltipTitle,
	user2Name,
	homeUrl,
	activeBackgroundClass = "bg-gray-100",
	iconClassName,
	showOrgNavCard = true,
	showAppNavCard = true,
	showOrgNavSelector = true,
	showAppNavSelector = true,
	avatar1Src,
	avatar1Alt,
	avatar1Initials,
	avatar2Src,
	avatar2Alt,
	avatar2Initials,
	avatar1BackgroundColor,
	avatar2BackgroundColor,
	headerText,
	availableOrganizations,
	currentOrganization,
	disabled = false,
	userAvatarSrc,
	isPlatformAdmin = false,
	isOrgAdmin = false,
	mainAppNavCardOnClickHandler,
	adminAppNavCardOnClickHandler,
	variant = "light",
	logoSrc,
	logoIconSrc,
	accountMode,
}) => {
	const isBrand = variant === "brand";

	let leftNavBorderColor = "#213A52";
	if (isOrgAdmin) {
		leftNavBorderColor = "#F7941D";
	} else if (isPlatformAdmin) {
		leftNavBorderColor = "#939598";
	}

	// Render `children` (Outlet + Toaster) exactly once by selecting container
	// chrome at the React level. The Tailwind `hidden lg:block` / `lg:hidden`
	// classes are kept as belt-and-suspenders for the single render frame
	// between a matchMedia change and the resulting re-render.
	// See docs/todo/SHELL-DUPLICATE-CHILDREN-FIX.md for the trade-off (subtree
	// unmounts/remounts when the viewport crosses the `lg` breakpoint at
	// runtime).
	const isLg = useBreakpoint("lg");

	return (
		// Side accent border (`border-l-[7px]` + role color) toggled off for now.
		<div className="h-screen flex flex-col overflow-hidden">
			{isLg && !isBrand && (
				<div className="hidden lg:block">
					<HeaderNavigation
						items={[]}
						avatarSrc={userAvatarSrc}
						name={name}
						email={email}
						onSignOut={onSignOut}
						onUserProfile={onUserProfile}
						homeUrl={homeUrl}
						leftNavBorderColor={leftNavBorderColor}
					/>
				</div>
			)}

			<div className="flex lg:flex-1 overflow-hidden">
				<LeftNav
					items={items}
					user1Name={user1Name}
					orgNavTooltipTitle={orgNavTooltipTitle}
					user2Name={user2Name}
					homeUrl={homeUrl}
					activeBackgroundClass={activeBackgroundClass}
					iconClassName={iconClassName}
					showOrgNavCard={showOrgNavCard}
					showAppNavCard={showAppNavCard}
					showOrgNavSelector={showOrgNavSelector}
					showAppNavSelector={showAppNavSelector}
					avatar1Src={avatar1Src}
					avatar1Alt={avatar1Alt}
					avatar1Initials={avatar1Initials}
					avatar2Src={avatar2Src}
					avatar2Alt={avatar2Alt}
					avatar2Initials={avatar2Initials}
					avatar1BackgroundColor={avatar1BackgroundColor}
					avatar2BackgroundColor={avatar2BackgroundColor}
					headerText={headerText}
					name={name}
					email={email}
					onSignOut={onSignOut}
					onUserProfile={onUserProfile}
					availableOrganizations={availableOrganizations}
					currentOrganization={currentOrganization}
					disabled={disabled}
					isPlatformAdmin={isPlatformAdmin}
					isOrgAdmin={isOrgAdmin}
					mainAppNavCardOnClickHandler={mainAppNavCardOnClickHandler}
					adminAppNavCardOnClickHandler={adminAppNavCardOnClickHandler}
					variant={variant}
					logoSrc={logoSrc}
					logoIconSrc={logoIconSrc}
					accountMode={accountMode}
				/>

				{isLg && (
					<main className="flex-1 min-h-0 overflow-auto hidden lg:block">
						<div>{children}</div>
					</main>
				)}
			</div>

			{!isLg && (
				<div className="lg:hidden flex-1 overflow-auto">
					<div>{children}</div>
				</div>
			)}
		</div>
	);
};

export default Shell;
